import React, { Component, useEffect, useRef } from 'react';
import { Container, Row, Col, Card, Button } from 'react-bootstrap';
import {
  ReactFlow,
  ReactFlowProvider,
  Controls,
  Background,
  BackgroundVariant,
  applyNodeChanges,
  applyEdgeChanges,
  useReactFlow,
} from '@xyflow/react';
import { GoPlus, GoInfo } from 'react-icons/go';
import { GrRevert } from "react-icons/gr";
import { TfiSave } from "react-icons/tfi";
import { BiExpandHorizontal, BiCollapseHorizontal } from "react-icons/bi";

import Tooltip from "../../../controls/common/tooltip";

import LibraryPane from "../../../controls/library/index";
import { removeComponentById } from "../../widgets/common/widgetBase";
import DetailsPane from "../../details/index";

import ChatNode from "../../widgets/chat/index";
import GraphNode from "../../widgets/graph/index";
import HtmlNode from "../../widgets/html/index";
import MessageNode from "../../widgets/message/index";
import MessagesNode from "../../widgets/messages/index";
import ThreadNode from "../../widgets/thread/index";
import TradingViewNode from "../../widgets/tradingview/index";
import MissionsNode from "../../widgets/missionControl/missions/index";

import { cascadeOffset } from "../../../controls/library/placement";
import Converter from "./converter";
import "./index.css";

const FlowWrapper = ({app, shell, shown, ...rest}) => {
  const converter = new Converter(shell.shellInterface);
  const reactFlowInstance = useReactFlow();
  const wasShownRef = useRef(shown);
  const shellRef = useRef(shell);
  shellRef.current = shell;
  // React Flow's current selection, captured from onSelectionChange. shell.nodes/edges do NOT carry
  // the transient `selected` flag, so this is the reliable source for an Esc-driven deselect.
  const selectedNodesRef = useRef([]);
  const selectedEdgesRef = useRef([]);

  // When the playground definition has a persisted zoom, honour it on
  // mount: cap fitView to that zoom so the auto-fit centres content
  // *at* the saved zoom level instead of overriding it.
  const persistedZoom = shell?.definitionObjectBackup?.layout?.zoom;
  const hasPersistedZoom = typeof persistedZoom === "number" && persistedZoom > 0;
  const fitViewOptions = hasPersistedZoom
    ? { padding: 0.1, minZoom: persistedZoom, maxZoom: persistedZoom }
    : { padding: 0.1 };

  useEffect(() => {
    // ReactFlow's initial `fitView` runs at mount; if the playground is
    // hidden (display: none) at that time, the canvas reports zero
    // dimensions and the centering is wrong. Re-fit when the tab becomes
    // visible.
    if (shown && !wasShownRef.current) {
      const id = setTimeout(() => {
        try { reactFlowInstance.fitView(fitViewOptions); } catch (e) {}
      }, 0);
      wasShownRef.current = shown;
      return () => clearTimeout(id);
    }
    wasShownRef.current = shown;
  }, [shown, reactFlowInstance, fitViewOptions]);

  // Apply the shell's zoom (driven from the details-pane slider) to the
  // ReactFlow viewport when it changes externally.
  const zoom = shell.zoom ?? 1;
  useEffect(() => {
    if (!shown) return;
    try {
      const current = reactFlowInstance.getZoom();
      if (Math.abs(current - zoom) > 0.001) {
        reactFlowInstance.zoomTo(zoom);
      }
    } catch (e) {
      // ignore — instance not ready yet
    }
  }, [zoom, shown, reactFlowInstance]);

  const nodeTypes = {
    "widget.chat": ChatNode,
    "widget.graph": GraphNode,
    "widget.html": HtmlNode,
    "widget.message": MessageNode,
    "widget.messages": MessagesNode,
    "widget.thread": ThreadNode,
    "widget.tradingview": TradingViewNode,
    "app.missions.control": MissionsNode,
  };

  const onNodesChange = (changes) => {
    // Mark workflow as modified if impacted
    const isModified = converter.doReactFlowChangesImpactDefinition(changes);

    // Ignore changes occurring
    // - revision is not editable
    // - while the component is hidden
    if ( isModified && ( !shell.shellInterface.canEditRevision() || !shown ) ) {
      return;
    }

    // Apply changes
    shell.workflowInterface.setNodes(applyNodeChanges(changes, shell.nodes), isModified);
  };

  const onEdgesChange = (changes) => {
    // Mark workflow as modified if impacted
    const isModified = converter.doReactFlowChangesImpactDefinition(changes);

    // Ignore changes occurring
    // - revision is not editable
    // - while the component is hidden
    if ( isModified && ( !shell.shellInterface.canEditRevision() || !shown ) ) {
      return;
    }

    // Apply changes
    shell.workflowInterface.setEdges(applyEdgeChanges(changes, shell.edges), isModified);
  };

  const onDragOver = (event) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = "move";
  };

  const { screenToFlowPosition } = reactFlowInstance;

  const onDrop = (event) => {
    let componentDefinition = {};

    try {
      // Retrieve component definition from the event
      componentDefinition = JSON.parse(event.dataTransfer.getData("text/plain"));
    } catch {
      return;
    }

    // Prevent default action
    event.preventDefault();

    // Determine position of the dropped element
    const position = screenToFlowPosition({
      x: event.clientX,
      y: event.clientY,
    });

    // Create new node
    const node = converter.createNewNode(componentDefinition, position, shell.nodes);

    // Add node to the global list
    shell.workflowInterface.setNodes(shell.nodes.concat(node), true);
  };

  // React Flow can momentarily drop a node's selection while the user interacts with form controls rendered
  // inside the node (e.g. a widget's own inputs — the graph create dialog, details panel, or seed field). Keep
  // the widget selected (and its header visible) as long as focus remains inside the currently-selected
  // component, so typing inside a widget never hides its header.
  // Kept in a ref so the mount-once Delete handler below reads the current closure, not the first one.
  const isFocusInsideSelectedComponentRef = useRef(null);
  const isFocusInsideSelectedComponent = () => {
    const state = shell.shellInterface.getState ? shell.shellInterface.getState() : null;
    if ( !state || !state.details || state.details.view !== "component" ) return false;
    const id = state.details.data && state.details.data.id;
    const active = typeof document !== "undefined" ? document.activeElement : null;
    if ( !id || !active || !active.closest ) return false;
    // Focus inside a widget-spawned modal (e.g. the graph create dialog, which portals to <body>) also counts
    // as "inside the widget" for selection-retention purposes.
    if ( active.closest(".modal") ) return true;
    const escapedId = ( typeof CSS !== "undefined" && CSS.escape ) ? CSS.escape(id) : id;
    return !!active.closest(`.react-flow__node[data-id="${escapedId}"]`);
  };
  isFocusInsideSelectedComponentRef.current = isFocusInsideSelectedComponent;

  // Narrower cousin of the above, for the Delete shortcut.
  //
  // isFocusInsideSelectedComponent() answers "should the widget STAY selected", so it counts focus on
  // the React Flow node element itself. That is the wrong question for Delete: React Flow makes nodes
  // focusable, so clicking a widget to select it puts focus ON the node wrapper — and treating that as
  // "inside the widget" made the shortcut bail every time, which is why Delete worked in grid (whose
  // cells are not focusable) and never in free-form.
  //
  // What Delete needs to know is whether focus is in the widget's CONTENT — a descendant of the node,
  // or a widget-spawned modal. Focus resting on the node wrapper is selection, and Delete is exactly
  // the key you expect to work then.
  const isFocusInsideComponentContent = () => {
    const state = shell.shellInterface.getState ? shell.shellInterface.getState() : null;
    if ( !state || !state.details || state.details.view !== "component" ) return false;
    const id = state.details.data && state.details.data.id;
    const active = typeof document !== "undefined" ? document.activeElement : null;
    if ( !id || !active || !active.closest ) return false;
    if ( active.closest(".modal") ) return true;
    const escapedId = ( typeof CSS !== "undefined" && CSS.escape ) ? CSS.escape(id) : id;
    const nodeEl = active.closest(`.react-flow__node[data-id="${escapedId}"]`);
    // Focus outside the node entirely, or on the node wrapper itself (= selection), is not "content".
    return !!nodeEl && active !== nodeEl;
  };
  const isFocusInsideComponentContentRef = useRef(null);
  isFocusInsideComponentContentRef.current = isFocusInsideComponentContent;

  const onSelectionChange = (params) => {
    // Remember what ReactFlow currently considers selected so Esc can deselect it.
    selectedNodesRef.current = params.nodes;
    selectedEdgesRef.current = params.edges;
    if ( 1 === params.nodes.length && 0 === params.edges.length ) {
      shell.shellInterface.updateDetails("component", params.nodes[0]);
    } else if ( 0 === params.nodes.length && 1 === params.edges.length ) {
      shell.shellInterface.updateDetails("connection", params.edges[0]);
    } else if ( !isFocusInsideSelectedComponent() ) {
      shell.shellInterface.updateDetails(null, null);
    }
  }

  // Mirror ReactFlow's native zoom into the shell so the details-pane
  // slider reflects the actual viewport zoom. onMoveEnd fires once when
  // the gesture (or programmatic change like fitView) settles.
  //
  // - User-initiated (event !== null): use setZoom — this marks the
  //   playground modified so the Save button surfaces.
  // - Programmatic (event === null, e.g. fitView on first mount): write
  //   the value through updateState directly so the slider matches what
  //   the user sees, without flagging the playground as modified.
  const onMoveEnd = (event, viewport) => {
    if (!viewport) return;
    if (event) {
      shell.shellInterface.setZoom?.(viewport.zoom);
    } else {
      shell.shellInterface.updateState?.({ zoom: viewport.zoom });
    }
  };

  // Esc deselects the active node/edge. Grid has its own document Esc handler; free-form had none.
  // This is also where an Escape forwarded out of a sandboxed widget.html iframe (re-dispatched as a
  // native keydown) lands. We blur the active element first so the "keep selected while focus is inside"
  // guard in onSelectionChange doesn't block the deselect, and clear React Flow's own selection so its
  // state stays in sync with the shell.
  useEffect(() => {
    const onKeyDown = (event) => {
      if ( event.key !== "Escape" ) return;
      const tag = event.target && event.target.tagName;
      if ( tag === "INPUT" || tag === "TEXTAREA" || (event.target && event.target.isContentEditable) ) return;
      const currentShell = shellRef.current;
      // shell.nodes/edges do not carry ReactFlow's transient `selected` flag — use the selection
      // captured from onSelectionChange instead.
      const nodeChanges = selectedNodesRef.current.map((n) => ({ id: n.id, type: "select", selected: false }));
      const edgeChanges = selectedEdgesRef.current.map((e) => ({ id: e.id, type: "select", selected: false }));
      const detailsSelected = !!(currentShell.details && currentShell.details.view);
      if ( nodeChanges.length === 0 && edgeChanges.length === 0 && !detailsSelected ) return;
      if ( document.activeElement && typeof document.activeElement.blur === "function" ) {
        document.activeElement.blur();
      }
      if ( nodeChanges.length ) {
        currentShell.workflowInterface.setNodes(applyNodeChanges(nodeChanges, currentShell.nodes), false);
      }
      if ( edgeChanges.length ) {
        currentShell.workflowInterface.setEdges(applyEdgeChanges(edgeChanges, currentShell.edges), false);
      }
      // Guarantee the header/details clear even if ReactFlow doesn't round-trip the deselect.
      currentShell.shellInterface.updateDetails(null, null);
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, []);

  // Delete/Backspace removes the selected widget.
  //
  // React Flow's own deleteKeyCode stays disabled (see the prop below) because it fires on any Delete
  // that bubbles up from inside a widget — a Delete pressed while editing a step in the Mission
  // Control workflow editor would take the whole widget with it. This handler restores the shortcut
  // with the guard that failure mode needs: the key only removes the widget when focus is NOT inside
  // it. Select a widget by clicking its frame and Delete removes it; click into its content first and
  // Delete belongs to that content.
  useEffect(() => {
    const onKeyDown = (event) => {
      if ( event.key !== "Delete" && event.key !== "Backspace" ) return;
      const tag = event.target && event.target.tagName;
      if ( tag === "INPUT" || tag === "TEXTAREA" || (event.target && event.target.isContentEditable) ) return;
      // Focus inside the widget's CONTENT (a nested editor, a widget-spawned modal) — the key is
      // theirs. Focus on the node wrapper itself is just selection; see isFocusInsideComponentContent.
      if ( isFocusInsideComponentContentRef.current() ) return;

      const currentShell = shellRef.current;
      if ( !currentShell.shellInterface.canEditRevision() ) return;
      const details = currentShell.details;
      if ( !details || details.view !== "component" || !details.data || !details.data.id ) return;

      event.preventDefault();
      removeComponentById(currentShell.shellInterface, details.data.id);
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, []);

  return (
    <ReactFlow
      nodes={shell.nodes}
      edges={shell.edges}
      onNodesChange={onNodesChange}
      onEdgesChange={onEdgesChange}
      onDrop={onDrop}
      onDragOver={onDragOver}
      onSelectionChange={onSelectionChange}
      onMoveEnd={onMoveEnd}
      nodeTypes={nodeTypes}
      // The playground canvas is itself a React Flow whose nodes are the widgets. With the default
      // deleteKeyCode, a Delete/Backspace pressed inside a nested widget (e.g. selecting a step in the
      // Mission Control workflow editor) bubbles up to this canvas and removes the whole selected widget
      // node. Disable the key entirely — widgets are removed via the header "×" (WidgetBase.removeComponent),
      // never the keyboard — so a Delete inside a widget only ever affects that widget's own content. There
      // are no widget-to-widget connections to delete here (the converter always serialises connections: []).
      deleteKeyCode={null}
      fitView
      fitViewOptions={fitViewOptions}
      attributionPosition="bottom-right"
      {...rest}
    >
      <Controls
        position="bottom-left"
        showInteractive={false}
      />
      <Background id="playground-background" variant={BackgroundVariant.Dots} gap={12} size={1} />
    </ReactFlow>
  )
}

export default class Workspace extends Component {
  addCount = 0;
  surfaceRef = React.createRef();

  handleReactFlowInit = (instance) => {
    this.rfInstance = instance;
  };

  handleAddComponent = (componentDef) => {
    if (!this.rfInstance) return;
    if (!this.props.shell.shellInterface.canEditRevision()) return;

    // Place at the center of the visible canvas (not the window), so the new
    // node lands centered even with the library/details panes taking width.
    const rect = this.surfaceRef.current?.getBoundingClientRect();
    const screenPoint = rect
      ? { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 }
      : { x: window.innerWidth / 2, y: window.innerHeight / 2 };
    const base = this.rfInstance.screenToFlowPosition(screenPoint);
    const { dx, dy } = cascadeOffset(this.addCount++);
    const position = { x: base.x + dx, y: base.y + dy };

    const converter = new Converter(this.props.shell.shellInterface);
    const node = converter.createNewNode(componentDef, position, this.props.shell.nodes);
    this.props.shell.workflowInterface.setNodes(this.props.shell.nodes.concat(node), true);
  };

  render() {
    // Disable editing entirely if
    // - revision is not editable
    const disabled = !this.props.shell.shellInterface.canEditRevision();

    const containerContentClass
      = this.props.shell.wideContent || this.props.shell.library.visible || this.props.shell.details.visible
      ? "content-row-wide"
      : "content-row";

    return (
      <>
        <Container className={"blob-header-row " + containerContentClass}>
          <Row>
            <Col sm={3} className={`detail-panel-col ${this.props.shell.library.visible ? "" : "hidden"}`}>
              <LibraryPane shell={this.props.shell} onAddComponent={this.handleAddComponent} />
            </Col>

            <Col>
              <Card>
                <Card.Body>
                  <div className="workflow-controls-pane-left">
                    {!this.props.shell.library.visible &&
                      <Button variant="light" onClick={() => {
                        this.props.shell.shellInterface.updatePane("library", true);
                      }}><GoPlus/></Button>
                    }
                  </div>

                  <div className="workflow-controls-pane-right">
                    {this.props.shell.modified &&
                      <>
                        <Tooltip text="Revert all changes">
                          <Button variant="light" onClick={() => {
                            this.props.shell.shellInterface.revertPlayground();
                          }}><GrRevert /></Button>
                        </Tooltip>
                        &nbsp;&nbsp;
                        <Tooltip text="Save changes">
                          <Button variant="light" onClick={() => {
                            this.props.shell.shellInterface.savePlayground();
                          }}><TfiSave /></Button>
                        </Tooltip>
                        &nbsp;&nbsp;
                      </>
                    }

                    <Tooltip text="Toggle width">
                      <Button variant="light" onClick={() => {
                        this.props.shell.shellInterface.toggleWideContent();
                      }}>
                        {this.props.shell.wideContent ? <BiCollapseHorizontal /> : <BiExpandHorizontal />}
                      </Button>
                    </Tooltip>

                    {!this.props.shell.details.visible &&
                      <>
                        &nbsp;&nbsp;
                        <Tooltip text="Show details">
                          <Button variant="light" onClick={() => {
                            this.props.shell.shellInterface.updatePane("details", true);
                          }}><GoInfo/></Button>
                        </Tooltip>
                      </>
                    }
                  </div>

                  <div className="playground-editor playground-editor-surface" ref={this.surfaceRef}>
                    <ReactFlowProvider key="playground-editor">
                      <FlowWrapper
                        app={this.props.app}
                        shell={this.props.shell}
                        shown={this.props.shown}
                        onInit={this.handleReactFlowInit}
                        edgesUpdatable={!disabled}
                        edgesFocusable={!disabled}
                        nodesDraggable={!disabled}
                        nodesConnectable={!disabled}
                        nodesFocusable={!disabled}
                        zoomOnDoubleClick={!disabled}
                      />
                    </ReactFlowProvider>
                  </div>
                </Card.Body>
              </Card>
            </Col>

            <Col sm={3} className={`detail-panel-col ${this.props.shell.details.visible ? "" : "hidden"}`}>
              <DetailsPane shell={this.props.shell} timezone={this.props.timezone} />
            </Col>
          </Row>
        </Container>
      </>
    );
  }
}
